import React from "react";
import ReactDOMServer from "react-dom/server";
import TrimbleMaps from "@trimblemaps/trimblemaps-js";
import { getSVGImage, getImageData } from "./utils";
import { isEqual } from "lodash/lang";
import { withMapContext } from "../MapContext";
import appConstants from "../../../../../appConstants";

class MarkerLayer extends React.Component {
  constructor(props) {
    super(props);
    const layerId = `_${this.props.layerId}` || ""; // if map has multiple layers, this will be unique for each layer
    this.markerSource = "markerSource_" + this.props.mapId + layerId; 
    this.markerLayer = "markerLayer_" + this.props.mapId + layerId;
    this.markerLayerBadge = "markerLayerBadge_" + this.props.mapId + layerId;
    this.markerTooltip = "markerTooltip_" + this.props.mapId + layerId;
    this.markerImages = []; // Tracks list of images created & added to map
    this.trimbleMarkers = []; // Tracks all marker object references (for non svg images)- to delete on unmoubnt
  }

  componentDidMount() {
    if (this.props.isStyleLoaded && this.props.data.length) {
      this.renderMarkerLayer();
    }
  }

  componentDidUpdate(prevProps) {
    // replace source data if active markers changed or actual marker list changes
    // this will rerender markers without needing to add or delete layers
  
    const layerExists = Boolean(this.props.map.getLayer(this.markerLayer));
    if (layerExists && (!isEqual(prevProps.activeMarkerIds, this.props.activeMarkerIds)  || !isEqual(prevProps.data, this.props.data) )) {
      this.replaceMakerSourceData(this.props.data);
    } else if (
      this.props.isStyleLoaded &&
      this.props.data.length &&
      !layerExists
    ) {
      this.renderMarkerLayer();
    }
    // Focus on active center
    if (this.props.center && (!isEqual(this.props.center, prevProps.center) || this.props.applyOffset !== prevProps.applyOffset)) {
      this.props.easeToAnimation
      ? this.props.map.easeTo({
          center: this.props.center,
          zoom: appConstants.MAP_ZOOM_LEVEL
      }) :
      this.props.map.flyTo({
        center: this.props.center,
        offset: this.props.centerOffset || [0, 0],
        zoom: appConstants.MAP_ZOOM_LEVEL,
      });
    }
  }

  componentWillUnmount() {
    this.props.map.off("click", this.markerLayer, this.markerLayerClicked);
    this.props.map.off("mousemove", this.markerLayer, this.markerLayerMouseMoved);
    this.props.map.off("mouseleave", this.markerLayer, this.markerLayerMouseLeave);
    this.removeTrimbleMarkers();
    this.removeMarkerLayer();
  }

  removeMarkerLayer = () => {
    if (this.props.map._removed) {
      return;
    }
    // Remove the layer first
    if (this.props.map.getLayer(this.markerLayer)) {
      this.props.map.removeLayer(this.markerLayer);
    }
    if (this.props.map.getLayer(this.markerLayerBadge)) {
      this.props.map.removeLayer(this.markerLayerBadge);
    }
    // Remove the source
    if (this.props.map.getSource(this.markerSource)) {
      this.props.map.removeSource(this.markerSource);
    }
  };

  // Helper function to extract text color from SVG
  extractTextColorFromSvg = (svgString) => {
    try {
      const container = document.createElement("div");
      container.innerHTML = svgString;
      const svg = container.getElementsByTagName("svg")[0];
      if (!svg) return null;
      const textNodes = svg.getElementsByTagName("text");
      if (textNodes.length > 0) {
        const firstTextNode = textNodes[0];
        return firstTextNode.getAttribute("fill") || firstTextNode.style.fill || "#000000";
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  // Helper function to strip text nodes from SVG
  stripTextFromSvg = (svgString) => {
    try {
      const container = document.createElement("div");
      container.innerHTML = svgString;
      const svg = container.getElementsByTagName("svg")[0];
      if (!svg) return svgString;
      const textNodes = svg.getElementsByTagName("text");
      [...textNodes].forEach(node => node.parentNode && node.parentNode.removeChild(node));
      return new XMLSerializer().serializeToString(svg);
    } catch (e) {
      return svgString;
    }
  };

  // Helper function to register an image to the map
  registerImageToMap = (iconKey, svgString) => {
    if (this.markerImages.includes(iconKey) || this.props.map.hasImage(iconKey)) {
      return;
    }

    this.markerImages.push(iconKey);
    
    getSVGImage(svgString)
      .then(getImageData)
      .then((imageData) => {
        if (imageData) {
          this.props.map.addImage(iconKey, imageData);
        }
      });
  };

  // Helper function to create marker properties
  createMarkerProperties = (markerData, iconKey, detail, badgeLabel, textColor) => {
    return {
      id: markerData[this.props.dataKey],
      icon: iconKey,
      tooltipHTML: this.props.showTooltip ?
        JSON.parse(detail["data-tooltip"])?.filter(Boolean).join("<br/>") : undefined,
      markerLabel: this.props.getMarkerLabel?.(markerData),
      badgeLabel: badgeLabel,
      badgeTextColor: textColor
    };
  };

  addIconImageToMap = (markerData) => {
    const markerIcon = this.props.getIcon(markerData);
    const string = ReactDOMServer.renderToString(markerIcon);
    const detail = markerIcon.props.children.props;

    // If a marker is of type svg & non active , then render them as images
    if (string.includes("<svg") && !this.isMarkerActive(markerData)) {
      const markerName = detail["data-markername"];
      const badgeLabel = this.props.getBadgeLabel?.(markerData);
      
      // Check if this is a badged SVG by looking for the class
      const isBadgedSvg = string.includes('class="badged-marker-svg"');
      
      // Extract text color from SVG before stripping
      const textColor = isBadgedSvg ? this.extractTextColorFromSvg(string) : null;
      
      // Create different image keys for markers with and without badges
      const iconKey = isBadgedSvg ? `${markerName}_with_badge` : markerName;
      
      if (isBadgedSvg) {
        this.registerImageToMap(iconKey, this.stripTextFromSvg(string));
      }else{
        this.registerImageToMap(iconKey, string);
      }

      return this.createMarkerProperties(markerData, iconKey, detail, badgeLabel, textColor);
    } else {
      // If a marker is not of type svg or is active (to show this icon on top of all other icons) , then render them as images
      this.addTrimbleMarker(markerData, markerIcon);
      return null;
    }
  };

  isMarkerActive = (markerData) => {
    const { dataKey, activeMarkerIds } = this.props;
    if (!markerData || !activeMarkerIds?.length) return false;
    const markerId = markerData[dataKey];
    if (markerId == null) return false;
    const markerIdLower = String(markerId).toLowerCase();
    return activeMarkerIds.some(
      (id) => id != null && String(id).toLowerCase() === markerIdLower
    );
  };

  addTrimbleMarker = (markerData, markerIcon) => {
    // Create a trimble marker by creating a local div & rendering the icons into it
    const markerContainer = document.createElement("div");
    markerContainer.id = "marker_" + markerData[this.props.dataKey];
    markerContainer.style = `cursor:pointer; z-index: ${this.isMarkerActive(markerData) ? 2 : 0};`; //Z-Index higher than terminal marker when marker is selected
    markerContainer.onmousemove = this.markerMouseMoved(markerData[this.props.dataKey]);
    markerContainer.onclick = (event) => {
      event.stopPropagation();
      this.markerClicked(markerData[this.props.dataKey]);
    };
    ReactDOM.render(markerIcon, markerContainer);
    const marker = new TrimbleMaps.Marker({
      element: markerContainer,
    })
      .setLngLat({ lat: markerData.latitude, lng: markerData.longitude })
      .addTo(this.props.map);
    this.trimbleMarkers.push(marker);
  };

  markerMouseMoved = (id) => (event) => {
    if (!this.props.showOnHoverPopup) {
      // prevents both marker & symbol tooltip appearing when they overlap
      event.stopPropagation();
    }
    //added this code to show popup on hover for active markers based on props
    if(this.props.showOnHoverPopup && this.props.onMouseHover && id){
    const currentZoom = this.props.map.getZoom();
    const thresholdZoomLevel = this.props.thresholdZoomLevel ?? appConstants.MAP_ZOOM_LEVEL;
    if (currentZoom >= thresholdZoomLevel) {
      const markerData = this.props.data.find(
        (marker) => marker[this.props.dataKey] === id
      );
      if (markerData) {
        this.props.onMouseHover(markerData);
      }
    }
   }      
  }

  // Helper function to create the main marker layer
  createMainMarkerLayer = (layout, paint) => {
    this.props.map.addLayer({
      id: this.markerLayer,
      source: this.markerSource,
      type: "symbol",
      layout: {
        "icon-image": ["get", "icon"], // reads icon  from properties of source data
        // get the marker label from the source's "markerLabel" property and show label only when map zoom level >= threshold zoom level 
        "text-field": ["step", ["zoom"], "", this.props.thresholdZoomLevel ?? appConstants.MAP_ZOOM_LEVEL, ["get", "markerLabel"]],
        "icon-rotation-alignment": "map",
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "text-allow-overlap": true,
        "text-font": ["Roboto Regular"],
        ...layout
      },
      paint: {
        ...paint
      }
    });
  };

  // Helper function to create the badge text layer
  createBadgeTextLayer = (badgeLayout, badgePaint) => {
    // Define the dynamic color logic once
    const textColor = [
      "case",
      ["has", "badgeTextColor"],
      ["get", "badgeTextColor"],
      "#000000"
    ];

    this.props.map.addLayer({
      id: this.markerLayerBadge,
      source: this.markerSource,
      type: "symbol",
      layout: {
        "icon-image": ["get", "icon"],
        "text-field": [
          "case",
          ["has", "badgeLabel"],
          ["get", "badgeLabel"],
          ""
        ],
        "text-anchor": "center",
        "text-offset": [1, -0.8],
        "text-allow-overlap": true,
        "icon-allow-overlap": true,
        "text-font": ["Roboto Light"],
        "text-size": 9,
        ...badgeLayout
      },
      paint: {
        "text-halo-color": textColor,
        "text-color": textColor,
        "text-halo-width": 0.5,
        ...badgePaint
      }
    });
  };

  // Helper function to attach event listeners to layers
  attachLayerEventListeners = () => {
    this.props.map.on("click", this.markerLayer, this.markerLayerClicked);
    this.props.map.on("mousemove", this.markerLayer, this.markerLayerMouseMoved);
    this.props.map.on("mouseleave", this.markerLayer, this.markerLayerMouseLeave);
  };

  renderMarkerLayer = () => {
    if (!this.props.map){     
      return;
    }

    this.markerImages = [];
    this.addMarkerSource(this.props.data);
    
    // Create layers with custom styling if provided
    const { layout, paint } = this.props.markerLabelProps || {};
    const { badgeLayout, badgePaint } = this.props.badgeLabelProps || {};
    this.createMainMarkerLayer(layout, paint);
    this.createBadgeTextLayer(badgeLayout, badgePaint);
    this.attachLayerEventListeners();
  };

  addMarkerSource = (data) => {
    const markerSourceData = {
      type: "FeatureCollection",
      features: data
        .map((markerData) => {
          const markerProperties = this.addIconImageToMap(markerData);
          if (!markerProperties) {
            return null; // skip active & non svg markers as we create trimble map markers for them
          }
          return {
            type: "Feature",
            properties:markerProperties,
            geometry: {
              type: "Point",
              coordinates: [markerData.longitude, markerData.latitude],
            },
          };
        })
        .filter(Boolean),
    };
    const markerSource = this.props.map.getSource(this.markerSource);

    // Update the data source when already exists,
    // this happens on active marker list change or terminal change
    if (markerSource) {
      markerSource.setData(markerSourceData);
    } else {
      const markerGeojson = {
        type: "geojson",
        data: markerSourceData,
      };     
      this.props.map.addSource(this.markerSource, markerGeojson);
    }
  };

  replaceMakerSourceData = () => {
    this.removeTrimbleMarkers();
    this.addMarkerSource(this.props.data);
  };

  removeTrimbleMarkers = () => {
    this.trimbleMarkers.map((marker) => marker.remove());
    this.trimbleMarkers = [];
  };

  onMouseHover = (event) => {
    const currentZoom = this.props.map.getZoom();
    const thresholdZoomLevel = this.props.thresholdZoomLevel ?? appConstants.MAP_ZOOM_LEVEL;
    if (currentZoom >= thresholdZoomLevel) {
      const id = event.features.find(
        (feature) => feature.layer.id === this.markerLayer && feature.layer.type === 'symbol'
      )?.properties?.id;
      const markerData = this.props.data.find((marker) => marker[this.props.dataKey] === id);
      if (markerData) {
        this.props.onMouseHover(markerData);
      }
    }
  };

  markerLayerClicked = (event) => {
    const id = event.features.find(
      (feature) =>
        feature.layer.id === this.markerLayer && feature.layer.type === "symbol"
    )?.properties?.id;
    this.markerClicked(id);
  };

  markerClicked = (id) => {
    const markerData = this.props.data.find(
      (marker) => marker[this.props.dataKey] === id
    );
    if (markerData) {
      this.props.onClick(markerData);
    }
  };

  markerLayerMouseMoved = (event) => {
    this.props.map.getCanvas().style.cursor = "pointer";
    if (this.props.showTooltip) {
      event.features.length ? this.showTooltip(event) : this.hideTooltip();
    }
    if(this.props.showOnHoverPopup && this.props.onMouseHover){
     event.features.length && this.onMouseHover(event);
    }
  };

  markerLayerMouseLeave = () => {
    this.props.map.getCanvas().style.cursor = "grab";
    this.hideTooltip();
  };

  showTooltip = (event) => {
   
    const properties = event.features.find(
      (feature) =>
        feature.layer.id === this.markerLayer && feature.layer.type === "symbol"
    )?.properties;

    if (!properties.tooltipHTML) {
      return
    }

    let tooltip = document.getElementById(this.markerTooltip);
    if(tooltip){
        if(tooltip.getAttribute("data-markerTooltipId") === properties.id){
          return;
        }
         tooltip.remove();
    }

    // Tooltip is shown here by creating a div element as a child of document.body & attach cursor position as absolute position of div

    tooltip = document.createElement("div");
    tooltip.id = this.markerTooltip;
    tooltip.className ="markerLayerTooltip"
      
    tooltip.setAttribute("data-markerTooltipId", properties.id);
    tooltip.style.position ="absolute";
    tooltip.style.transform = `translate3d(${event.point.x + "px"},${event.point.y + 10 + "px"},0px)`    
    tooltip.style.willChange ="transform"; 
    tooltip.style.zIndex = 1500;
    tooltip.innerHTML = properties.tooltipHTML;
    document.getElementById(this.props.mapId).appendChild(tooltip)
  }

  hideTooltip = () => {
     document.getElementById(this.markerTooltip)?.remove();
  }

  render() {
    return null;
  }
}

export default withMapContext(MarkerLayer)
